import { useState, useEffect } from "react";
import { Box, Flex, Heading, HStack, Image, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { TiArrowRight } from "react-icons/ti";
import { trackLandingPageView } from "../core/analytics";

import Logo from "../assets/images/logo.png";
import LogoShort from "../assets/images/logoShort.png";
import CustomerImg from "../assets/images/customer.png";
import HairDresserImg from "../assets/images/hairDresser.png";

const MotionBox = motion(Box);

export const Landing = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<"left" | "right" | null>(null);

  useEffect(() => {
    trackLandingPageView();
  }, []);

  const handleImageClick = (side: "left" | "right") => {
    if (selected === side) {
      setSelected(null);
    } else {
      setSelected(side);
    }
  };

  return (
    <Box
      minH="100vh"
      bg={{
        base: "linear-gradient(to bottom, #FFF7F3, #D8F1F2)",
        lg: "linear-gradient(to right, #FFF7F3, #D8F1F2)",
      }}
      py={"30px"}
      px={{ base: "16px", lg: "50px" }}
    >
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        mb={{ base: "40px", lg: "90px" }}
        maxW="1700px"
        mx="auto"
      >
        <HStack gap="8px" cursor="pointer" onClick={() => navigate("/")}>
          <Image
            display={{ base: "block", lg: "none" }}
            src={LogoShort}
            h="40px"
            objectFit="contain"
          />

          <Image
            display={{ base: "none", lg: "block" }}
            src={Logo}
            h="40px"
            objectFit="contain"
          />
        </HStack>

        <HStack gap={0} display={{ base: "none", lg: "flex" }}>
          <Flex
            as="button"
            onClick={() => navigate("/stylist")}
            bg="rgba(0, 0, 0, 0.05)"
            borderTopLeftRadius="30px"
            borderBottomLeftRadius="30px"
            pl="16px"
            pr={7}
            pt="12px"
            pb="8px"
            align="center"
            cursor="pointer"
            _hover={{ bg: "#FFFFFF" }}
            marginRight={-5}
          >
            <Text
              fontSize="16px"
              fontWeight={600}
              color="#19202D"
              fontFamily="LeagueSpartan"
            >
              Book-d Pros
            </Text>
          </Flex>

          <Flex
            as="button"
            onClick={() => navigate("/client")}
            bg="#FF8E3C"
            borderRadius="30px"
            px="16px"
            pt="12px"
            pb="8px"
            align="center"
            cursor="pointer"
            _hover={{ bg: "#D4633D" }}
          >
            <Text
              fontSize="16px"
              fontWeight={600}
              color="#fff"
              fontFamily="LeagueSpartan"
            >
              Get Book-d
            </Text>
          </Flex>
        </HStack>
      </Flex>

      {/* Title */}
      <Heading
        fontSize={{ base: "32px", lg: "52px" }}
        color="#19202D"
        fontFamily="Comfortaa"
        textAlign="center"
        mb={{ base: "30px", lg: "50px" }}
        lineHeight="110%"
      >
        Book <Box as="br" display={{ base: "block", lg: "none" }} />
        Black Beauty Services
      </Heading>

      {/* Desktop Grid */}
      <Flex
        display={{ base: "none", lg: "flex" }}
        gap="20px"
        h="520px"
        maxW="1700px"
        mx="auto"
      >
        {/* Left Image - Professionals */}
        <MotionBox
          position="relative"
          borderRadius="20px"
          overflow="hidden"
          onHoverStart={() => handleImageClick("left")}
          onHoverEnd={() => handleImageClick("left")}
          animate={{
            flex: selected === "left" ? 2 : selected === "right" ? 1 : 1,
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{ flex: 1 }}
        >
          <Image
            src={HairDresserImg}
            w="100%"
            h="100%"
            objectFit="cover"
            borderRadius="20px"
          />
          <MotionBox
            position="absolute"
            bottom="24px"
            left="24px"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Flex
              as="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                navigate("/stylist");
              }}
              bg="white"
              borderRadius="30px"
              px="20px"
              pt="12px"
              pb="8px"
              align="center"
              gap="8px"
              cursor="pointer"
              boxShadow="0 2px 12px rgba(0,0,0,0.15)"
              _hover={{ bg: "#F5F5F5" }}
              border="1px solid"
              borderColor="#FF8E3C"
            >
              <Text
                fontSize="16px"
                fontWeight={600}
                color="#19202D"
                fontFamily="LeagueSpartan"
              >
                Become a Pro
              </Text>

              <TiArrowRight size={24} color="#19202D" />
            </Flex>
          </MotionBox>
        </MotionBox>

        {/* Right Image - Book a service */}
        <MotionBox
          position="relative"
          borderRadius="20px"
          overflow="hidden"
          onHoverStart={() => handleImageClick("right")}
          onHoverEnd={() => handleImageClick("right")}
          animate={{
            flex: selected === "right" ? 2 : selected === "left" ? 1 : 1,
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{ flex: 1 }}
        >
          <Image
            src={CustomerImg}
            w="100%"
            h="100%"
            objectFit="cover"
            borderRadius="20px"
          />
          <MotionBox
            position="absolute"
            bottom="24px"
            right="24px"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Flex
              as="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                navigate("/client");
              }}
              bg="white"
              borderRadius="30px"
              px="20px"
              pt="12px"
              pb="8px"
              align="center"
              gap="8px"
              cursor="pointer"
              boxShadow="0 2px 12px rgba(0,0,0,0.15)"
              _hover={{ bg: "#F5F5F5" }}
              border="1px solid"
              borderColor="#FF8E3C"
            >
              <Text
                fontSize="17px"
                fontWeight={600}
                color="#19202D"
                fontFamily="LeagueSpartan"
              >
                Book a service
              </Text>
              <TiArrowRight size={24} color="#19202D" />
            </Flex>
          </MotionBox>
        </MotionBox>
      </Flex>

      {/* Mobile Stack */}
      <Flex
        display={{ base: "flex", lg: "none" }}
        flexDir="column"
        gap="20px"
        mx="auto"
      >
        {/* Mobile - Professionals */}
        <Box position="relative" borderRadius="20px" overflow="hidden">
          <Image
            src={HairDresserImg}
            w="100%"
            h="300px"
            objectFit="cover"
            borderRadius="20px"
          />
          <Flex
            position="absolute"
            bottom="20px"
            left="50%"
            transform="translateX(-50%)"
          >
            <Flex
              as="button"
              onClick={() => navigate("/stylist")}
              bg="white"
              borderRadius="30px"
              px="20px"
              pt="12px"
              pb="8px"
              align="center"
              gap="8px"
              cursor="pointer"
              boxShadow="0 2px 12px rgba(0,0,0,0.15)"
              border="1px solid"
              borderColor="#FF8E3C"
            >
              <Text
                fontSize="17px"
                fontWeight={600}
                color="#19202D"
                fontFamily="LeagueSpartan"
              >
                Become a Pro
              </Text>
              <TiArrowRight size={20} color="#19202D" />
            </Flex>
          </Flex>
        </Box>

        {/* Mobile - Book a service */}
        <Box position="relative" borderRadius="20px" overflow="hidden">
          <Image
            src={CustomerImg}
            w="100%"
            h="300px"
            objectFit="cover"
            borderRadius="20px"
          />
          <Flex
            position="absolute"
            bottom="20px"
            left="50%"
            transform="translateX(-50%)"
          >
            <Flex
              as="button"
              onClick={() => navigate("/client")}
              bg="white"
              borderRadius="30px"
              px="20px"
              py="12px"
              align="center"
              gap="8px"
              cursor="pointer"
              boxShadow="0 2px 12px rgba(0,0,0,0.15)"
              border="1px solid"
              borderColor="#FF8E3C"
            >
              <Text
                fontSize="16px"
                fontWeight={600}
                color="#19202D"
                fontFamily="LeagueSpartan"
              >
                Book a service
              </Text>
              <TiArrowRight size={20} color="#19202D" />
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};
